import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { ImageUpload } from '@/components/image-upload'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  LOCALES,
  trendingSchema,
  useSaveTrending,
  type LocaleCode,
  type TrendingInput,
  type TrendingItem,
  type TrendingRow,
} from '@/features/trending/api'

/** Every locale key present, so react-hook-form has a field behind each tab. */
function toFormValues(item?: TrendingRow): TrendingInput {
  const pane = (code: LocaleCode) => ({
    title: item?.locales[code]?.title ?? '',
    body: item?.locales[code]?.body ?? '',
  })

  return {
    // Absent for a new entry: the server assigns MAX+1 so it lands last. Sending
    // a number here would tear the dense 1..N sequence.
    position: item?.position,
    trending_value: item?.trending_value ?? 0,
    cover_url: item?.cover_url ?? '',
    link_url: item?.link_url ?? '',
    status: item?.status ?? 'draft',
    locales: { en: pane('en'), 'zh-tw': pane('zh-tw'), ko: pane('ko'), ja: pane('ja') },
  }
}

/**
 * The form always holds all four locale panes so every tab has fields, but a
 * pane left untouched must not be sent. Persisting `{title: '', body: ''}` would
 * create an empty row in trending_item_locales, and the consumer site would
 * then render a blank title for that locale instead of falling back to English.
 */
function stripBlankLocales(values: TrendingInput): TrendingInput {
  const locales = { ...values.locales }
  for (const { code, required } of LOCALES) {
    if (required) continue
    const pane = locales[code]
    if (!pane?.title.trim() && !pane?.body.trim()) delete locales[code]
  }
  return { ...values, locales }
}

interface TrendingEditorProps {
  /** Absent for a new entry. */
  item?: TrendingRow
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The saved row, with the position the SERVER assigned. The list cannot work
   * this out for itself: a create lands at MAX+1, which is off the current page
   * whenever the list is longer than one page.
   */
  onSaved?: (row: TrendingItem) => void
}

export function TrendingEditor({ item, open, onOpenChange, onSaved }: TrendingEditorProps) {
  const save = useSaveTrending()

  /*
   * Set when the server clamped the position to something other than what was
   * typed. There is no upper bound on the input on purpose — only the server
   * knows N — so typing 150 into a 20-row list is silently answered with 20.
   * Saying so is the difference between a clamp and a shrug.
   */
  const [clamped, setClamped] = useState<{ asked: number; got: number } | null>(null)

  const form = useForm<TrendingInput>({
    resolver: standardSchemaResolver(trendingSchema),
    defaultValues: toFormValues(item),
  })

  /*
   * Read during render, deliberately, and this is load-bearing.
   *
   * react-hook-form wraps formState in a Proxy that only begins tracking a slice
   * once that slice has been read while rendering. Reading dirtyFields only
   * inside the submit handler leaves it permanently empty — which silently
   * stripped `position` from EVERY patch and made the position input do nothing
   * at all. Caught by driving the screen; tsc and eslint both saw nothing wrong.
   */
  const { dirtyFields } = form.formState

  /*
   * Surfaced below the tabs, not only beside the English input: the operator may
   * be on another locale's tab when they submit, and that pane is unmounted, so
   * an error rendered inside it would be invisible.
   */
  const localeError =
    form.formState.errors.locales?.en?.title?.message ??
    form.formState.errors.locales?.en?.body?.message

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Reset on close so reopening for a different row does not inherit the
        // previous row's values.
        if (!next) {
          form.reset(toFormValues(item))
          save.reset()
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit trending entry' : 'New trending entry'}</DialogTitle>
          <DialogDescription>
            Cover image, localised title and body, trending value, and publish status.{' '}
            {item ? (
              <>Position swaps the entry with the one already in that slot.</>
            ) : (
              <>New entries are added to the end of the list.</>
            )}{' '}
            <b>Manual publish only</b> — entries cannot be scheduled in Phase 1.
          </DialogDescription>
        </DialogHeader>

        <form
          id="trending-form"
          className="space-y-5"
          onSubmit={form.handleSubmit((values) => {
            const input = stripBlankLocales(values)

            /*
             * Position rides along only when the operator actually touched it.
             *
             * The form snapshots `item.position` when the dialog opens and used
             * to resubmit it whatever had happened meanwhile — so if someone
             * else deleted an earlier row while this dialog sat open, saving an
             * unrelated field silently dragged the entry back to its old slot.
             * The server treats an absent position as "leave it alone".
             */
            if (!dirtyFields.position) delete input.position

            const asked = input.position
            setClamped(null)
            save.mutate(
              { id: item?.id, input },
              {
                onSuccess: (row) => {
                  onSaved?.(row)
                  /*
                   * A clamp is not a failure and not a prompt: the write already
                   * happened and the entry IS at row.position. So the dialog stays
                   * open only to say where it went, and the input is rewritten to
                   * the real value — marked clean, so simply closing does nothing
                   * further and an unrelated re-save will not resend the number
                   * that was just rejected.
                   */
                  if (asked !== undefined && row.position !== asked) {
                    setClamped({ asked, got: row.position })
                    form.reset({ ...form.getValues(), position: row.position })
                    return
                  }
                  onOpenChange(false)
                },
              },
            )
          })}
        >
          <div>
            <ImageUpload
              value={form.watch('cover_url')}
              onChange={(url) =>
                form.setValue('cover_url', url, { shouldDirty: true, shouldValidate: true })
              }
              purpose="trending"
              label="Cover image"
              aspectClassName="aspect-[16/9] max-w-xs"
            />
            {form.formState.errors.cover_url && (
              <p className="mt-1 text-xs text-destructive">
                {form.formState.errors.cover_url.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="link_url">Link URL</Label>
            <Input
              id="link_url"
              type="url"
              placeholder="https://example.com/article"
              {...form.register('link_url')}
            />
            <p className="text-xs text-faint">
              Where the reader lands on the consumer site after clicking this entry.
            </p>
            {form.formState.errors.link_url && (
              <p className="text-xs text-destructive">{form.formState.errors.link_url.message}</p>
            )}
          </div>

          <div className={item ? 'grid gap-5 sm:grid-cols-3' : 'grid gap-5 sm:grid-cols-2'}>
            {/*
              Only shown when editing. A new entry has no position to type — the
              server appends it to the end of the list — and an empty input on the
              create form would just invite someone to fight the sequence.
            */}
            {item && (
              <div className="space-y-2">
                <Label htmlFor="position">Position</Label>
                <Input id="position" type="number" min={1} {...form.register('position')} />
                <p className="text-xs text-faint">
                  1 is first. The entry already in that slot takes this one&rsquo;s place; no
                  other row renumbers. <b>Console ordering only</b> — the public feed sorts by
                  trending value.
                </p>
                {form.formState.errors.position && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.position.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="trending_value">Trending value</Label>
              <Input
                id="trending_value"
                type="number"
                min={0}
                max={10000}
                {...form.register('trending_value')}
              />
              <p className="text-xs text-faint">
                0–10000. This is what orders the public feed.
              </p>
              {form.formState.errors.trending_value && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.trending_value.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="trending-status">Status</Label>
              <Select
                value={form.watch('status')}
                onValueChange={(value) =>
                  form.setValue('status', value as TrendingInput['status'], { shouldDirty: true })
                }
              >
                <SelectTrigger id="trending-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              Content <span className="font-bold text-destructive">· English required</span>
            </Label>
            <Tabs defaultValue="en">
              <TabsList>
                {LOCALES.map(({ code, label, required }) => (
                  <TabsTrigger key={code} value={code}>
                    {label}
                    {required && ' *'}
                  </TabsTrigger>
                ))}
              </TabsList>

              {LOCALES.map(({ code, label, required }) => (
                <TabsContent key={code} value={code} className="space-y-3 pt-3">
                  <Input
                    placeholder={
                      required ? 'Title — required' : `Title (${label}) — optional`
                    }
                    aria-label={`Title (${code})`}
                    {...form.register(`locales.${code}.title` as const)}
                  />
                  <Textarea
                    rows={5}
                    placeholder={`Body (${label})`}
                    aria-label={`Body (${code})`}
                    {...form.register(`locales.${code}.body` as const)}
                  />
                </TabsContent>
              ))}
            </Tabs>
            <p className="text-xs text-faint">
              English is mandatory. Empty locales fall back to English on the site.
            </p>
            {localeError && <p className="text-xs text-destructive">{localeError}</p>}
          </div>

          {clamped && (
            <p className="text-xs text-faint">
              <b>Saved.</b> The list has{' '}
              {clamped.got === 1 ? '1 entry' : `${clamped.got} entries`}, so position{' '}
              {clamped.asked} became <b>{clamped.got}</b> — the entry is last. Close, or pick a
              different number.
            </p>
          )}

          {save.isError && (
            <p className="text-xs text-destructive">{(save.error as Error).message}</p>
          )}
        </form>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="trending-form" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
